import { common } from 'src/theme/palette';

// ── Drawer ──────────────────────────────────────────────────────────────────
export const drawerPaperStyle = {
    width: { xs: '100%', sm: 600 },
    p: 0,
    borderRadius: 0,
    bgcolor: '#F8FAFC',
};

export const drawerHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2.5,
    py: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    position: 'sticky',
    top: 0,
    bgcolor: '#fff',
    zIndex: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export const drawerHeaderIconStyle = {
    width: 36,
    height: 36,
    borderRadius: 1.5,
    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
};

export const drawerBodyStyle = {
    px: 2.5,
    py: 2.5,
    overflowY: 'auto',
    flex: 1,
};

// ── Section Card ────────────────────────────────────────────────────────────
export const sectionCardStyle = {
    bgcolor: '#fff',
    borderRadius: 2.5,
    p: 2,
    mb: 2,
    border: '1px solid',
    borderColor: 'rgba(145,158,171,0.12)',
    boxShadow: '0 1px 4px rgba(145,158,171,0.08)',
};

export const sectionLabelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: 'text.disabled',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    mb: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
};

// ── Book Appointment step sections (2026-08-17) ──────────────────────────────
// One shared look for the Treatment / Team / Availability sections of the
// explicit Book Appointment flow, so the Dentally and CRM-only modes read as the
// same screen. Deliberately NOT reusing sectionCardStyle/sectionLabelStyle:
// those are shared with the availability drawer, patient-details step, confirm
// steps and the PMS flow, and retuning them would ripple well outside booking.
//
// Label matches the CRM step's section labels (V2BookingDetailsStep) — 13px,
// weight 600, sentence case — rather than the old 11px uppercase + numbered
// badge. The numbers were dropped because they implied a 3-step flow while the
// real wizard is 2 steps (Dentally) or 5 steps (CRM).
export const bookingStepLabelStyle = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1d1d1f',
    letterSpacing: '-0.005em',
    mb: 1,
};

export const bookingStepPanelStyle = {
    background: '#f8f9fa',
    borderRadius: '12px',
    p: 2,
    // Explicit: the grey fill is the grouping cue, so no outline. Stated rather
    // than omitted because this style also overrides components that ship their
    // own bordered bar (TreatmentFlowComponent) — leaving it unset let that
    // border survive and reintroduced the double-outline look.
    border: 'none',
};

// ── Chips / Presets ─────────────────────────────────────────────────────────
export const presetChipGroupStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
};

export const presetChipStyle = (isActive) => ({
    fontWeight: 600,
    fontSize: '12px',
    cursor: 'pointer',
    borderRadius: '8px',
    height: 32,
    transition: 'all 0.2s ease',
    ...(isActive
        ? {
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(24,144,255,0.3)',
            border: '1px solid transparent',
            '&:hover': {
                background: 'linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)',
                boxShadow: '0 4px 12px rgba(24,144,255,0.35)',
            },
        }
        : {
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'rgba(145,158,171,0.24)',
            color: 'text.secondary',
            '&:hover': {
                bgcolor: 'rgba(24,144,255,0.04)',
                borderColor: 'rgba(24,144,255,0.3)',
                color: 'primary.main',
            },
        }),
});

// ── Search Button ───────────────────────────────────────────────────────────
export const searchButtonStyle = {
    borderRadius: 2,
    py: 1.25,
    fontWeight: 700,
    fontSize: '14px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    boxShadow: '0 4px 14px rgba(24,144,255,0.3)',
    '&:hover': {
        background: 'linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)',
        boxShadow: '0 6px 20px rgba(24,144,255,0.4)',
    },
    '&.Mui-disabled': {
        background: 'rgba(145,158,171,0.24)',
        boxShadow: 'none',
    },
};

export const searchProgressBarStyle = {
    mb: 2,
    borderRadius: 4,
    height: 6,
    bgcolor: 'rgba(24,144,255,0.1)',
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        background: 'linear-gradient(90deg, #1890ff, #40a9ff)',
    },
};

// ── Results Header ──────────────────────────────────────────────────────────
export const resultsHeaderStyle = {
    bgcolor: '#fff',
    borderRadius: 2.5,
    px: 2,
    py: 1.5,
    mb: 2,
    border: '1px solid',
    borderColor: 'rgba(145,158,171,0.12)',
    boxShadow: '0 1px 4px rgba(145,158,171,0.08)',
};

// ── Date Group ──────────────────────────────────────────────────────────────
export const dateGroupHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1.25,
    px: 0.5,
};

export const dateGroupBadgeStyle = {
    fontSize: '10px',
    fontWeight: 700,
    height: 20,
    borderRadius: '6px',
    bgcolor: 'rgba(24,144,255,0.08)',
    color: 'primary.main',
    border: '1px solid rgba(24,144,255,0.15)',
};

// ── Time Period Section ─────────────────────────────────────────────────────
export const timePeriodSectionStyle = {
    mb: 1.5,
};

export const timePeriodHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    mb: 0.75,
    mt: 0.25,
    px: 0.5,
};

export const timePeriodPillStyle = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.25,
    borderRadius: '6px',
    bgcolor: `${color}10`,
});

export const timePeriodBadgeStyle = {
    fontSize: '10px',
    fontWeight: 600,
    height: 18,
    minWidth: 18,
    borderRadius: '4px',
    bgcolor: 'rgba(145,158,171,0.08)',
    color: 'text.disabled',
};

// ── Slot Card ───────────────────────────────────────────────────────────────
export const slotCardStyle = (isSelected, accentColor) => ({
    py: 1.25,
    px: 1.5,
    borderRadius: '10px',
    border: '1px solid',
    borderColor: isSelected ? 'primary.main' : 'rgba(145,158,171,0.12)',
    borderLeft: `3px solid ${isSelected ? '#1890FF' : (accentColor || 'rgba(145,158,171,0.2)')}`,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    bgcolor: isSelected ? 'rgba(24,144,255,0.04)' : '#fff',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isSelected
        ? '0 0 0 1px rgba(24,144,255,0.15), 0 2px 8px rgba(24,144,255,0.1)'
        : '0 1px 2px rgba(145,158,171,0.06)',
    '&:hover': {
        borderColor: isSelected ? 'primary.main' : 'rgba(24,144,255,0.4)',
        bgcolor: isSelected ? 'rgba(24,144,255,0.06)' : 'rgba(24,144,255,0.02)',
        boxShadow: '0 2px 8px rgba(24,144,255,0.1)',
        transform: 'translateY(-1px)',
    },
});

export const slotTimeStyle = {
    fontFamily: '"SF Mono", "Roboto Mono", monospace',
    fontWeight: 700,
    fontSize: 13,
    color: 'text.primary',
    letterSpacing: '0.3px',
};

export const slotDurationChipStyle = {
    fontSize: '10px',
    height: 18,
    fontWeight: 600,
    borderRadius: '4px',
    bgcolor: 'rgba(145,158,171,0.08)',
    color: 'text.disabled',
};

export const slotSelectChipStyle = {
    fontSize: '11px',
    height: 28,
    fontWeight: 700,
    ml: 'auto',
    borderRadius: '8px',
    transition: 'all 0.15s ease',
};

export const slotPractitionerAvatarStyle = (colour) => ({
    width: 28,
    height: 28,
    fontSize: 10,
    fontWeight: 700,
    bgcolor: colour,
    flexShrink: 0,
});

// ── Empty State ─────────────────────────────────────────────────────────────
export const emptyStateBoxStyle = {
    textAlign: 'center',
    py: 6,
    px: 3,
    bgcolor: '#fff',
    borderRadius: 3,
    border: '1px dashed',
    borderColor: 'rgba(145,158,171,0.3)',
};

// ── Footer ──────────────────────────────────────────────────────────────────
export const drawerFooterStyle = {
    px: 2.5,
    py: 2,
    borderTop: '1px solid',
    borderColor: 'divider',
    bgcolor: '#fff',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
};

export const footerSummaryStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'rgba(34,197,94,0.06)',
    border: '1px solid rgba(34,197,94,0.15)',
    mb: 1.5,
};

export const confirmButtonStyle = {
    borderRadius: 2,
    py: 1.25,
    fontWeight: 700,
    fontSize: '14px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #22C55E 0%, #16a34a 100%)',
    boxShadow: '0 4px 14px rgba(34,197,94,0.3)',
    '&:hover': {
        background: 'linear-gradient(135deg, #4ade80 0%, #22C55E 100%)',
        boxShadow: '0 6px 20px rgba(34,197,94,0.4)',
    },
};

// ── Practitioner Autocomplete ───────────────────────────────────────────────
export const practitionerOptionStyle = {
    '&.MuiAutocomplete-option': { py: 0.75, px: 1.25 },
};

export const practitionerTagChipStyle = {
    maxWidth: 155,
    borderRadius: '8px',
    height: 28,
    fontWeight: 600,
    fontSize: '12px',
    border: '1px solid rgba(145,158,171,0.16)',
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: '0 1px 2px rgba(145,158,171,0.08)',
    '& .MuiChip-label': { color: 'text.primary' },
};

export const practitionerOverflowChipStyle = {
    fontWeight: 700,
    fontSize: '11px',
    height: 28,
    borderRadius: '8px',
    bgcolor: 'rgba(24,144,255,0.08)',
    color: 'primary.main',
    border: '1px solid rgba(24,144,255,0.15)',
};

export const selectAllRowStyle = (allSelected) => ({
    py: 0.75,
    px: 1.25,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid',
    borderColor: 'rgba(145,158,171,0.12)',
    bgcolor: allSelected ? 'rgba(24,144,255,0.04)' : '#fff',
    '&:hover': { bgcolor: 'rgba(24,144,255,0.06)' },
    position: 'sticky',
    top: 0,
    zIndex: 1,
});

// ── Patient Mapper ──────────────────────────────────────────────────────────
export const patientMapperCardStyle = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    mb: 2,
};

export const patientLinkedCardStyle = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'success.main',
    bgcolor: 'rgba(34,197,94,0.04)',
    mb: 2,
};

export const patientSearchResultStyle = (isSelected) => ({
    p: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: isSelected ? 'primary.main' : 'divider',
    cursor: 'pointer',
    bgcolor: isSelected ? 'primary.lighter' : 'transparent',
    transition: 'all 0.1s',
    '&:hover': { bgcolor: isSelected ? 'primary.lighter' : 'action.hover' },
});

// ── Payment Confirmation ────────────────────────────────────────────────────
export const paymentConfirmBtnStyle = (variant) => ({
    p: 2.5,
    borderRadius: 2,
    border: '2px solid',
    borderColor: variant === 'received' ? common.mintyZest : '#FFAB00',
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    bgcolor: 'transparent',
    '&:hover': {
        bgcolor: variant === 'received' ? 'rgba(0,171,85,0.04)' : 'rgba(255,171,0,0.04)',
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${variant === 'received' ? 'rgba(0,171,85,0.15)' : 'rgba(255,171,0,0.15)'}`,
    },
});

export const paymentConfirmBtnActiveStyle = (variant) => ({
    ...paymentConfirmBtnStyle(variant),
    bgcolor: variant === 'received' ? 'rgba(0,171,85,0.08)' : 'rgba(255,171,0,0.08)',
    boxShadow: `0 0 0 2px ${variant === 'received' ? common.mintyZest : '#FFAB00'}`,
});

export const appointmentSummaryCardStyle = {
    p: 2,
    borderRadius: 2,
    bgcolor: 'primary.lighter',
    border: '1px solid',
    borderColor: 'primary.light',
    mb: 2.5,
};

export const dentallyInfoCardStyle = (variant) => ({
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: variant === 'success' ? 'success.light' : 'warning.light',
    bgcolor: variant === 'success' ? 'rgba(34,197,94,0.04)' : 'rgba(255,171,0,0.04)',
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
});

// ── Journey / Stepper (Dentally Confirmation Page) ──────────────────────────

export const journeyContainerStyle = {
    px: { xs: 2, sm: 4, md: 6 },
    py: 2.5,
    maxWidth: 680,
    mx: 'auto',
    width: '100%',
};

export const journeySummaryBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'rgba(24,144,255,0.04)',
    border: '1px solid rgba(24,144,255,0.12)',
    mb: 3,
};

export const journeyStepIndicator = (status) => {
    const colors = {
        completed: { bg: '#22C55E', shadow: 'rgba(34,197,94,0.3)' },
        active: { bg: '#1890ff', shadow: 'rgba(24,144,255,0.3)' },
        pending: { bg: 'rgba(145,158,171,0.24)', shadow: 'none' },
    };
    const c = colors[status] || colors.pending;
    return {
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: c.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: c.shadow !== 'none' ? `0 2px 8px ${c.shadow}` : 'none',
        transition: 'all 0.3s ease',
    };
};

export const journeyConnectorStyle = (isActive) => ({
    width: 2,
    minHeight: 20,
    bgcolor: isActive ? '#22C55E' : 'rgba(145,158,171,0.2)',
    ml: '13px',
    transition: 'background-color 0.3s ease',
});

const stepCardBorderColors = {
    active: 'rgba(24,144,255,0.2)',
    completed: 'rgba(34,197,94,0.2)',
    pending: 'rgba(145,158,171,0.12)',
};

const stepCardBgColors = {
    active: '#fff',
    completed: 'rgba(34,197,94,0.03)',
    pending: 'rgba(145,158,171,0.04)',
};

export const journeyStepCardStyle = (status) => ({
    p: 2,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: stepCardBorderColors[status] || stepCardBorderColors.pending,
    bgcolor: stepCardBgColors[status] || stepCardBgColors.pending,
    boxShadow: status === 'active' ? '0 2px 12px rgba(24,144,255,0.08)' : 'none',
    transition: 'all 0.3s ease',
    mb: 0.5,
});

export const journeyPaymentBtnStyle = (variant, isActive) => {
    const isGreen = variant === 'received';
    const baseColor = isGreen ? '#22C55E' : '#FFAB00';
    const hoverBg = isGreen ? 'rgba(34,197,94,0.06)' : 'rgba(255,171,0,0.06)';
    const activeBg = isGreen ? 'rgba(34,197,94,0.08)' : 'rgba(255,171,0,0.08)';
    return {
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: baseColor,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: isActive ? activeBg : 'transparent',
        boxShadow: isActive ? `0 0 0 2px ${baseColor}, 0 4px 12px ${baseColor}20` : 'none',
        '&:hover': {
            bgcolor: isActive ? activeBg : hoverBg,
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 16px ${baseColor}25`,
        },
    };
};

export const journeyPatientResultStyle = (isSelected) => ({
    p: 1.25,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: isSelected ? 'primary.main' : 'rgba(145,158,171,0.16)',
    cursor: 'pointer',
    bgcolor: isSelected ? 'rgba(24,144,255,0.04)' : '#fff',
    transition: 'all 0.15s ease',
    boxShadow: isSelected ? '0 0 0 1px rgba(24,144,255,0.15)' : '0 1px 2px rgba(145,158,171,0.06)',
    '&:hover': {
        bgcolor: isSelected ? 'rgba(24,144,255,0.06)' : 'rgba(145,158,171,0.04)',
        borderColor: isSelected ? 'primary.main' : 'rgba(145,158,171,0.3)',
    },
});

export const journeyLinkedBadgeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    p: 1.5,
    borderRadius: 2,
    bgcolor: 'rgba(34,197,94,0.06)',
    border: '1px solid rgba(34,197,94,0.2)',
};

// Compact collapsed card for completed Step 1
export const journeyStepCollapsedStyle = {
    p: 1.25,
    borderRadius: 2,
    border: '1px solid rgba(34,197,94,0.2)',
    bgcolor: 'rgba(34,197,94,0.03)',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    mb: 0.5,
};
